<template>
  <div class="pivot-widget">
    <div class="widget-header">
      <h3 v-if="title">{{ title }}</h3>
      <button class="export-btn" @click="exportToExcel">Export XLSX</button>
    </div>
    <div class="table-scroll">
      <table class="pivot-table">
        <thead>
          <tr>
            <th class="col-name">{{ rowLabel }}</th>
            <th class="col-value">{{ valueLabel }}</th>
            <th class="col-percent">% {{ valueLabel }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in visibleRows"
            :key="row.path"
            :class="['pivot-row', 'depth-' + row.depth, { 'group-row': row.hasChildren, 'leaf-row': row.isLeaf, 'metric-cell': row.isLeaf && metrics.length }]"
            @click="row.hasChildren && toggle(row.path)"
            @mouseenter="row.isLeaf && onCellEnter(row, $event)"
            @mousemove="onCellMove" @mouseleave="onCellLeave"
          >
            <td class="col-name">
              <span class="indent" :style="{ paddingLeft: row.depth * 18 + 'px' }"></span>
              <span v-if="row.hasChildren" class="toggle-icon">{{ expanded[row.path] ? '−' : '+' }}</span>
              <span v-else class="toggle-icon leaf">·</span>
              <component :is="row.hasChildren ? 'strong' : 'span'">{{ row.name }}</component>
            </td>
            <td class="col-value">
              <component :is="row.hasChildren ? 'strong' : 'span'">{{ formatNum(row.total) }}</component>
            </td>
            <td class="col-percent">
              <component :is="row.hasChildren ? 'strong' : 'span'">{{ formatPct(row.total / grandTotal) }}</component>
            </td>
          </tr>
          <tr class="total-row">
            <td class="col-name"><strong>Total</strong></td>
            <td class="col-value"><strong>{{ formatNum(grandTotal) }}</strong></td>
            <td class="col-percent"><strong>100,00%</strong></td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Tooltip d'agregades de la fila fulla -->
    <div
      v-if="hoveredLeaf && leafMetricLines(hoveredLeaf).length"
      class="cell-tooltip"
      :style="{ top: tooltipPos.y + 'px', left: tooltipPos.x + 'px' }"
    >
      <strong>{{ hoveredLeaf.name }}</strong>
      <div class="tooltip-sep"></div>
      <div v-for="(ml, mi) in leafMetricLines(hoveredLeaf)" :key="mi" class="tooltip-metric">
        <span>{{ ml.name }}</span><b>{{ ml.value }}</b>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import * as XLSX from 'xlsx';

const props = defineProps({
  title: { type: String, default: '' },
  data: { type: Array, required: true },
  // Compat 2 nivells: fila (rowField) × fill (colField).
  rowField: { type: String, default: '' },
  colField: { type: String, default: '' },
  // N nivells: llista ordenada de camps a niar. Si té valors, preval sobre rowField/colField.
  levels: { type: Array, default: () => [] },
  valueField: { type: String, default: 'count' },
  rowLabel: { type: String, default: 'Grup' },
  valueLabel: { type: String, default: 'Valor' },
  // Agregades opcionals: només a fulles amb un únic bucket (nia per totes les dimensions).
  metrics: { type: Array, default: () => [] },
  metricLabels: { type: Object, default: () => ({}) },
  euro: { type: Boolean, default: false }
});

const METRIC_FUNC_LABELS = { SUM: 'Suma', AVG: 'Media', MIN: 'Mínimo', MAX: 'Máximo', MEDIAN: 'Mediana' };

const expanded = ref({});

// Camps de nivell efectius: la llista `levels` si en té, si no [rowField, colField].
const levelFields = computed(() => {
  const ls = (props.levels || []).filter(Boolean);
  if (ls.length) return ls;
  return [props.rowField, props.colField].filter(Boolean);
});

// Arbre niat per levelFields. Cada node: { name, total, children:Map, items:[], depth, path }.
const tree = computed(() => {
  const fields = levelFields.value;
  const root = { children: new Map(), total: 0 };
  (props.data || []).forEach(item => {
    const value = Number(item[props.valueField]) || 0;
    let node = root;
    node.total += value;
    fields.forEach((f, depth) => {
      const key = String(item[f] ?? 'N/A');
      if (!node.children.has(key)) {
        node.children.set(key, { name: key, total: 0, children: new Map(), items: [], depth, path: (node.path ? node.path + ' ▸ ' : '') + key });
      }
      node = node.children.get(key);
      node.total += value;
      if (depth === fields.length - 1) node.items.push(item);
    });
  });
  return root;
});

const grandTotal = computed(() => tree.value.total || 0);

// Llista plana de files visibles (segons expandits), ordenades per total desc a cada nivell.
const visibleRows = computed(() => {
  const out = [];
  const walk = (node) => {
    const children = Array.from(node.children.values()).sort((a, b) => b.total - a.total);
    for (const child of children) {
      const hasChildren = child.children.size > 0;
      out.push({
        path: child.path, name: child.name, total: child.total, depth: child.depth,
        hasChildren, isLeaf: !hasChildren, item: child.items.length === 1 ? child.items[0] : null
      });
      if (hasChildren && expanded.value[child.path]) walk(child);
    }
  };
  walk(tree.value);
  return out;
});

const hoveredLeaf = ref(null);
const tooltipPos = ref({ x: 0, y: 0 });
function onCellEnter(row, ev) {
  if (!props.metrics || !props.metrics.length || !row.item) return;
  hoveredLeaf.value = row;
  tooltipPos.value = { x: ev.clientX + 15, y: ev.clientY + 15 };
}
function onCellMove(ev) { if (hoveredLeaf.value) tooltipPos.value = { x: ev.clientX + 15, y: ev.clientY + 15 }; }
function onCellLeave() { hoveredLeaf.value = null; }

function leafMetricLines(row) {
  if (!props.metrics || !props.metrics.length || !row.item) return [];
  const lines = [];
  for (const m of props.metrics) {
    if (!m || !m.field || !Array.isArray(m.funcs)) continue;
    const leaf = String(m.field).split('.').pop();
    for (const func of m.funcs) {
      const expr = `${func}(${m.field})`;
      const cleanKey = expr.replace(/[\[\]" ]/g, '').toLowerCase();
      const name = props.metricLabels[cleanKey] || ((METRIC_FUNC_LABELS[func] || func) + ' ' + leaf);
      const v = row.item[expr];
      lines.push({ name, value: (v === undefined || v === null) ? '—' : formatNum(v) });
    }
  }
  return lines;
}

function toggle(path) { expanded.value[path] = !expanded.value[path]; }

function formatNum(val) {
  if (props.euro) {
    return Math.round(Number(val || 0)).toLocaleString('es-ES', { maximumFractionDigits: 0, useGrouping: 'always' }) + ' €';
  }
  return Number(val || 0).toLocaleString('es-ES');
}
function formatPct(val) {
  if (!val || isNaN(val)) return '0,00%';
  return (val * 100).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
}

function exportToExcel() {
  const fields = levelFields.value;
  const rows = [];
  const walk = (node, trail) => {
    const children = Array.from(node.children.values()).sort((a, b) => b.total - a.total);
    for (const child of children) {
      const nextTrail = [...trail, child.name];
      if (child.children.size > 0) {
        walk(child, nextTrail);
      } else {
        const row = {};
        fields.forEach((f, i) => { row[f] = nextTrail[i] ?? ''; });
        row[props.valueLabel] = child.total;
        rows.push(row);
      }
    }
  };
  walk(tree.value, []);
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Pivot');
  const safe = (props.title || 'pivot').replace(/[^a-z0-9]/gi, '_').toLowerCase();
  XLSX.writeFile(wb, `${safe}.xlsx`);
}
</script>

<style scoped>
.pivot-widget {
  background: white;
  border-radius: 4px;
  padding: 16px 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  border: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.widget-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  gap: 8px;
}

.widget-header h3 {
  font-size: 0.85rem;
  font-weight: 600;
  color: #333;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-family: 'Source Sans Pro', sans-serif;
}

.export-btn {
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid #059669;
  background-color: #10b981;
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}
.export-btn:hover { background-color: #059669; border-color: #047857; }

.table-scroll { flex: 1; overflow-y: auto; min-height: 0; }

.pivot-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }

.pivot-table th {
  text-align: left;
  padding: 10px 8px;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
  background: #f9fafb;
  position: sticky;
  top: 0;
  z-index: 5;
}

.pivot-table td {
  padding: 8px;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
}

.col-name { width: 50%; }
.col-value, .col-percent { width: 25%; text-align: right; }

.group-row { cursor: pointer; }
.pivot-row.depth-0 { background-color: #fafafa; }
.pivot-row.depth-0.group-row:hover { background-color: #f0f0f0; }
.pivot-row.depth-1 { background-color: #fdfdfd; }

.indent { display: inline-block; }
.toggle-icon {
  display: inline-block;
  width: 16px;
  margin-right: 6px;
  color: #6b7280;
  font-weight: bold;
  text-align: center;
}
.toggle-icon.leaf { color: #d1d5db; font-weight: normal; }

.col-name span:last-child { color: #4b5563; }
.pivot-row.depth-0 .col-name span:last-child { color: #111827; }

.total-row { background-color: #eff6ff; border-top: 2px solid #0b5394; }
.total-row td { color: #0b5394; padding: 12px 8px; }

.leaf-row.metric-cell { cursor: help; }
.leaf-row.metric-cell:hover td { background-color: #eef2ff; }

.cell-tooltip {
  position: fixed;
  background-color: rgba(17, 24, 39, 0.92);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  pointer-events: none;
  z-index: 1000;
  min-width: 160px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
}
.cell-tooltip .tooltip-sep { border-top: 1px solid rgba(255, 255, 255, 0.25); margin: 5px 0; }
.cell-tooltip .tooltip-metric { display: flex; justify-content: space-between; gap: 18px; line-height: 1.5; }
.cell-tooltip .tooltip-metric span { color: #cbd5e1; }
</style>
